// @flow
import * as React from 'react'
import { FormattedMessage } from 'mz-intl'
import logError from 'mz-utils/logError'
import cloneDatetimeWithoutTimezone from 'mz-utils/cloneDatetimeWithoutTimezone'
import getTrains, { type Train } from 'mz-sdk/services/train/getTrains'
import Button from '../../../core/Button'
import Form from '../../Form'
import DateField from '../../DateField/field'
import TrainStationField from '../../TrainStationField/field'
import TrainSearchResult from '../TrainSearchResult'
import { Container, Content, ButtonContainer } from './elements'
import messages from '../messages.intl'

// Types
import type { TrainInfo } from '../TrainRootField'

export type Appearance = 'desktop' | 'mobile'
export type Props = {
  trainInfo: TrainInfo,
  onSelect?: (Train) => any,
  finderAppearance?: Appearance,
  onClose?: () => any,
}
type State = {
  searchFinished: boolean,
  trains: Train[],
}

// Utils
const INITIAL_STATE = {
  searchFinished: false,
  trains: [],
}

function getFormInitialState(trainInfo: TrainInfo) {
  return {
    date: cloneDatetimeWithoutTimezone(trainInfo.pickupDatetime),
  }
}

/**
 * Wrapper around FlightField which adds a lightbox with
 * extra fields to find a flight. It manages all the state
 * internally.
 */
export class TrainFinderTool extends React.Component<Props, State> {
  state = INITIAL_STATE

  handleSelectTrain = (train: Train) => {
    const { onSelect, onClose } = this.props
    onSelect && onSelect(train)
    onClose && onClose()
  }

  handleSubmitFinder = async (vals: Object) => {
    const { trainDirection, stationCode } = this.props.trainInfo
    const stationsArr = [stationCode, vals.station.value]

    if (trainDirection === 'arr') {
      stationsArr.reverse()
    }

    try {
      const trains = await getTrains({
        ride_date: vals.date.format('YYYY-MM-DD'),
        ride_direction: trainDirection === 'dep' ? 'departing' : 'arriving',
        origin_station: stationsArr[0],
        destination_station: stationsArr[1],
      })

      this.setState({
        searchFinished: true,
        trains,
      })
    } catch (e) {
      logError(e)
      this.setState({
        searchFinished: true,
        trains: [],
      })
    }
  }

  renderSearchResults = () =>
    this.state.searchFinished && (
      <TrainSearchResult
        trainInfo={this.props.trainInfo}
        trains={this.state.trains}
        onSelectTrain={this.handleSelectTrain}
      />
    )

  renderFinderForm = ({ submit, meta }: *, nest: *) => {
    const stationLabel =
      this.props.trainInfo.trainDirection === 'arr'
        ? messages.ORIGIN_STATION
        : messages.DESTINATION_STATION

    const stationPlaceholder =
      this.props.trainInfo.trainDirection === 'arr'
        ? messages.ORIGIN_STATION_PLACEHOLDER
        : messages.DESTINATION_STATION_PLACEHOLDER

    return (
      <React.Fragment>
        <DateField
          {...nest('date')}
          lightboxSubtitle={null}
          fieldAppearance={this.props.finderAppearance}
          label={<FormattedMessage message={messages.TRAIN_DATE_LABEL} />}
          placeholder={
            <FormattedMessage message={messages.TRAIN_DATE_PLACEHOLDER} />
          }
          required
        />
        <TrainStationField
          {...nest('station')}
          fieldAppearance={this.props.finderAppearance}
          label={<FormattedMessage message={stationLabel} />}
          placeholder={<FormattedMessage message={stationPlaceholder} />}
          required
        />
        <ButtonContainer>
          <Button onClick={submit} loading={meta.deepLoading}>
            <FormattedMessage message={messages.FIND_TRAIN} />
          </Button>
        </ButtonContainer>
      </React.Fragment>
    )
  }

  render() {
    const styles = { $appearance: this.props.finderAppearance }
    const initialValue = getFormInitialState(this.props.trainInfo)

    return (
      <Container {...styles}>
        <Content {...styles}>
          <Form
            render={this.renderFinderForm}
            onSubmit={this.handleSubmitFinder}
            initValue={initialValue}
          />
          {this.renderSearchResults()}
        </Content>
      </Container>
    )
  }
}

export default TrainFinderTool
